import { useState, useEffect, useCallback } from "react";
import { Settings, Eye } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

function getTimeGreeting() {
  const h = new Date().getHours();
  if (h < 10) return "Guten Morgen";
  if (h < 17) return "Hallo";
  return "Guten Abend";
}

function resolvePreview(template, name, timeBased) {
  if (timeBased) return `${getTimeGreeting()} ${name}`;
  return template.replace(/\{name\}/g, name);
}

const TEMPLATES = [
  "Hallo {name}",
  "Servus {name}",
  "Willkommen {name}",
  "Moin {name}",
  "Hey {name}",
  "Servus {name}, guten Arbeitstag",
  "Willkommen zurueck, {name}",
];

export default function AppSettings({ user }) {
  const [template, setTemplate] = useState("Hallo {name}");
  const [nameFormat, setNameFormat] = useState("first_name");
  const [timeBased, setTimeBased] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/settings/greeting`);
      if (res.ok) {
        const data = await res.json();
        setTemplate(data.template || "Hallo {name}");
        setNameFormat(data.name_format || "first_name");
        setTimeBased(data.time_based ?? true);
      }
    } catch {}
    setLoaded(true);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/settings/greeting`, {
        method: "PATCH",
        body: JSON.stringify({ template, name_format: nameFormat, time_based: timeBased }),
      });
      if (res.ok) toast.success("Begruessung gespeichert");
      else toast.error("Fehler beim Speichern");
    } catch { toast.error("Fehler"); }
    setSaving(false);
  };

  const previewName = nameFormat === "full_name" ? (user?.name || "Florian Dotzler") : ((user?.name || "Florian Dotzler").split(" ")[0]);
  const preview = resolvePreview(template, previewName, timeBased);

  if (!loaded) return null;

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }} data-testid="greeting-settings">
      <div className="flex items-center gap-2.5 px-5 py-3" style={{ borderBottom: "1px solid var(--tga-border)" }}>
        <Settings className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
        <span className="text-[14px] font-semibold" style={{ color: "var(--tga-text-bright)" }}>Begruessung</span>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Preview */}
        <div className="rounded-lg px-4 py-3 border" style={{ backgroundColor: "var(--tga-bg-elevated)", borderColor: "var(--tga-border)" }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Eye className="w-3 h-3" style={{ color: "var(--tga-text-dim)" }} />
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "var(--tga-text-dim)" }}>Vorschau</span>
          </div>
          <p className="text-[16px] font-medium" style={{ color: "var(--tga-text-primary)" }} data-testid="greeting-preview">{preview}</p>
        </div>

        {/* Time-based toggle */}
        <label className="flex items-center justify-between cursor-pointer" data-testid="greeting-time-toggle">
          <div>
            <p className="text-[13px] font-medium" style={{ color: "var(--tga-text-primary)" }}>Zeitabhaengige Begruessung</p>
            <p className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>Morgen/Tag/Abend automatisch wechseln</p>
          </div>
          <input type="checkbox" checked={timeBased} onChange={(e) => setTimeBased(e.target.checked)}
            className="rounded border-gray-600 bg-white/10 text-blue-500 w-5 h-5" />
        </label>

        {/* Custom template (only shown when time-based is OFF) */}
        {!timeBased && (
          <div>
            <label className="block text-[12px] font-medium mb-1.5" style={{ color: "var(--tga-text-muted)" }}>Begrüssungstext</label>
            <input
              type="text"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              maxLength={200}
              className="w-full px-3 py-2 text-[13px] rounded-lg border focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{ backgroundColor: "var(--tga-bg-content)", borderColor: "var(--tga-border-strong)", color: "var(--tga-text-primary)" }}
              placeholder="Hallo {name}"
              data-testid="greeting-template-input"
            />
            <p className="text-[10px] mt-1" style={{ color: "var(--tga-text-dim)" }}>Platzhalter: {"{name}"} = Vorname oder voller Name</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {TEMPLATES.map((t) => (
                <button key={t} onClick={() => setTemplate(t)}
                  className="px-2 py-1 rounded text-[11px] border transition-colors"
                  style={{
                    borderColor: template === t ? "var(--tga-blue)" : "var(--tga-border)",
                    backgroundColor: template === t ? "rgba(59,130,246,0.1)" : "var(--tga-bg-elevated)",
                    color: template === t ? "var(--tga-blue)" : "var(--tga-text-secondary)",
                  }}
                  data-testid={`greeting-preset-${t.replace(/[^a-z]/gi, "").substring(0, 10)}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Name format */}
        <div>
          <label className="block text-[12px] font-medium mb-1.5" style={{ color: "var(--tga-text-muted)" }}>Namensformat</label>
          <div className="flex gap-2">
            <button onClick={() => setNameFormat("first_name")}
              className="flex-1 px-3 py-2 rounded-lg border text-[12px] font-medium transition-colors"
              style={{
                borderColor: nameFormat === "first_name" ? "var(--tga-blue)" : "var(--tga-border)",
                backgroundColor: nameFormat === "first_name" ? "rgba(59,130,246,0.1)" : "var(--tga-bg-elevated)",
                color: nameFormat === "first_name" ? "var(--tga-blue)" : "var(--tga-text-secondary)",
              }}
              data-testid="greeting-firstname-btn"
            >
              Nur Vorname
            </button>
            <button onClick={() => setNameFormat("full_name")}
              className="flex-1 px-3 py-2 rounded-lg border text-[12px] font-medium transition-colors"
              style={{
                borderColor: nameFormat === "full_name" ? "var(--tga-blue)" : "var(--tga-border)",
                backgroundColor: nameFormat === "full_name" ? "rgba(59,130,246,0.1)" : "var(--tga-bg-elevated)",
                color: nameFormat === "full_name" ? "var(--tga-blue)" : "var(--tga-text-secondary)",
              }}
              data-testid="greeting-fullname-btn"
            >
              Vorname + Nachname
            </button>
          </div>
        </div>

        {/* Save */}
        <button onClick={save} disabled={saving}
          className="w-full py-2.5 rounded-lg text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
          style={{ backgroundColor: "var(--tga-blue)" }}
          data-testid="greeting-save-btn"
        >
          {saving ? "Speichern..." : "Begruessung speichern"}
        </button>
      </div>
    </div>
  );
}
